import type {
  TimelineAnnotation,
  TimelineAnnotationDetails,
  TimelineAnnotationKind,
  TimelineAnnotationProposedAction,
  TimelineAnnotationSeverity,
  TimelineAnnotationSource,
  TimelineAnnotationSubtype,
} from "@/state/timeline/timeline.types";

export const AI_ANNOTATION_SUBTYPES: Record<TimelineAnnotationKind, TimelineAnnotationSubtype[]> = {
  creative: [],
  continuity: ["visual_continuity_mismatch", "emotional_drift", "spatial_break", "continuity_risk"],
  timing: ["beat_density_warning", "emotional_landing_adjustment", "rhythm_alignment_suggestion"],
  audio: ["audio_motif_proposal", "dialogue_clarity_issue", "sound_continuity_mismatch", "emotional_audio_treatment"],
  npc: ["npc_state_transition", "runtime_hook", "npc_continuity_warning"],
  intent: ["intent_inference", "intent_variant", "intent_drift"],
  publish: [],
};

export type BuildAiAnnotationRecordInput = {
  id?: string;
  nodeId?: string | null;
  frame: number;
  timeSec?: number | null;
  type: TimelineAnnotationKind;
  subtype: TimelineAnnotationSubtype;
  severity: TimelineAnnotationSeverity;
  summary: string;
  details: TimelineAnnotationDetails;
  proposedAction: TimelineAnnotationProposedAction;
  assetLabel: string;
  anchorTimecode: string;
  clipId?: string | null;
  linkedAudio?: boolean;
  action?: string | null;
  source?: TimelineAnnotationSource;
  createdAt?: number;
};

export function buildAiAnnotationRecord(input: BuildAiAnnotationRecordInput): TimelineAnnotation {
  return {
    id: input.id ?? `annotation-ai-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: input.createdAt ?? Date.now(),
    type: input.type,
    subtype: input.subtype,
    severity: input.severity,
    summary: input.summary,
    details: input.details,
    proposedAction: input.proposedAction,
    nodeId: input.nodeId ?? null,
    assetLabel: input.assetLabel,
    clipId: input.clipId ?? null,
    linkedAudio: !!input.linkedAudio,
    frameIndex: Math.max(0, Math.floor(input.frame)),
    anchorTimecode: input.anchorTimecode,
    timeSec: typeof input.timeSec === "number" && Number.isFinite(input.timeSec) ? input.timeSec : null,
    rawNote: input.summary,
    action: input.action?.trim() || null,
    source: input.source ?? "ai",
  };
}

export function isAiAnnotationRecord(annotation: TimelineAnnotation): boolean {
  return annotation.source === "ai" || !!annotation.subtype || !!annotation.details;
}